// Maquette d'estimation « action d'équipe » : données fictives (personnes, établissements, équipes, actions en attente) et règles des équipes.

export type TeamActionOptionId = 'equipes' | 'notifications';

export type TeamActionScreenKey =
  | 'team-list'
  | 'team-details'
  | 'support-establishment'
  | 'user-details'
  | 'purchase-actors'
  | 'establishment'
  | 'sub-basket'
  | 'team-mail'
  | 'bill'
  | 'open-questions'
  | 'recipients'
  | 'notification-preferences'
  | 'absence'
  | 'notification-list'
  | 'bell'
  | 'team-board'
  | 'action-list'
  | 'team-digest'
  | 'team-reminder'
  | 'team-lead'
  | 'mailbox';

/** Sous-panier délégué à une équipe, vu par un membre qui doit agir, pris en charge, ou ouvert depuis le mail. */
export type SubBasketMode = 'delegation' | 'action' | 'claim' | 'from-mail';
/** Destinataires : adresse commune seule, ou aussi les membres qui peuvent agir. */
export type RecipientsMode = 'shared-only' | 'who-can-act';
/** Boîte commune : suivi d'une action, ou objets normalisés. */
export type MailboxMode = 'follow-up' | 'subjects';
export type ScreenMode = SubBasketMode | RecipientsMode | MailboxMode;

export interface MockEstablishment {
  id: string;
  name: string;
  city: string;
}

export interface MockSupportEstablishment extends MockEstablishment {
  /** Établissements clients : le périmètre des équipes rattachées. */
  clients: MockEstablishment[];
}

export interface MockPerson {
  id: string;
  fullName: string;
  email: string;
  /** Rôles au sens de la 7.2 (ex-types d'acteur). */
  roles: string[];
  /** Établissements auxquels la personne est affectée. */
  establishments: MockEstablishment[];
}

/** Un endroit où une équipe est désignée. */
export interface MockTeamUsage {
  establishment: MockEstablishment;
  /** Famille d'achat, ou « Toutes les familles ». */
  scope: string;
  designation: string;
}

export interface MockTeam {
  id: string;
  name: string;
  email: string;
  role: string;
  support: MockSupportEstablishment;
  members: MockPerson[];
  usages: MockTeamUsage[];
}

export interface TeamActionScreen {
  key: TeamActionScreenKey;
  /** Une ligne affichée au-dessus de l'écran. */
  caption: string;
  mode?: ScreenMode;
  /** Numéro de la piste montrée (page Notifications). */
  piste?: number;
}

export interface TeamActionTab {
  id: string;
  label: string;
  screens: TeamActionScreen[];
}

export interface TeamActionOption {
  id: TeamActionOptionId;
  title: string;
  /** Une ligne pour la carte de l'accueil. */
  summary: string;
  principle: string;
  ready: boolean;
  tabs: TeamActionTab[];
  /** Titre de l'encadré de droite : « Les règles », « À savoir ». */
  asideTitle: string;
  /** Lignes de l'encadré de droite. */
  rules: string[];
}

/** Une action attendue, de quelqu'un ou d'une équipe (page Notifications). */
export interface MockPendingAction {
  id: string;
  action: string;
  reference: string;
  establishment: MockEstablishment;
  team?: MockTeam;
  person?: MockPerson;
  since: string;
  waitingDays: number;
  claimedBy?: MockPerson;
  claimedAt?: string;
}

export interface OpenQuestion {
  question: string;
  /** Hypothèse retenue par la maquette. */
  note?: string;
}

export const ROLE_VALIDATOR = 'Validateur';
const ROLE_ACCOUNTANT = 'Comptable';
const ROLE_SUPERVISION = 'Supervision métier';
const ROLE_APPROVER = 'Approbateur facture';

/** Rôles 7.2 proposés à la création d'une équipe (une équipe porte un seul rôle). */
export const TEAM_ROLES: string[] = [
  ROLE_VALIDATOR,
  ROLE_ACCOUNTANT,
  ROLE_SUPERVISION,
  ROLE_APPROVER,
  'Référent engagement',
  'Approvisionneur',
];

const TILLEULS: MockEstablishment = { id: 'tilleuls', name: 'Résidence Les Tilleuls', city: 'Nancy' };
const ACACIAS: MockEstablishment = { id: 'acacias', name: 'Résidence Les Acacias', city: 'Metz' };
const LAC: MockEstablishment = { id: 'lac', name: 'Résidence du Lac', city: 'Épinal' };

const VOSGES: MockEstablishment = { id: 'vosges', name: 'Résidence Les Vosges', city: 'Colmar' };
const RHIN: MockEstablishment = { id: 'rhin', name: 'Résidence du Rhin', city: 'Mulhouse' };

export const MOCK_ESTABLISHMENTS = { tilleuls: TILLEULS, acacias: ACACIAS, lac: LAC, vosges: VOSGES, rhin: RHIN };

export const MOCK_SUPPORT: MockSupportEstablishment = {
  id: 'spl',
  name: 'Services partagés Lorraine',
  city: 'Nancy',
  clients: [TILLEULS, ACACIAS],
};

const ALSACE_SUPPORT: MockSupportEstablishment = {
  id: 'spa',
  name: 'Services partagés Alsace',
  city: 'Colmar',
  clients: [VOSGES, RHIN],
};

/** Établissements de type Fonction support proposés à la création d'une équipe. */
export const MOCK_SUPPORTS: MockSupportEstablishment[] = [MOCK_SUPPORT, ALSACE_SUPPORT];

function person(id: string, fullName: string, roles: string[], establishments: MockEstablishment[]): MockPerson {
  const email = `${fullName.toLowerCase().replace(' ', '.').normalize('NFD').replace(/[̀-ͯ]/g, '')}@exemple.fr`;
  return { id, fullName, email, roles, establishments };
}

export const MOCK_PEOPLE = {
  paul: person('paul', 'Paul Renaud', [ROLE_VALIDATOR], [ACACIAS]),
  marie: person('marie', 'Marie Dupré', [ROLE_VALIDATOR, ROLE_ACCOUNTANT], [TILLEULS]),
  sophie: person('sophie', 'Sophie Lambert', [ROLE_VALIDATOR], [LAC]),
  claire: person('claire', 'Claire Petit', [ROLE_VALIDATOR], [TILLEULS, ACACIAS]),
  julien: person('julien', 'Julien Morel', [ROLE_ACCOUNTANT], [ACACIAS]),
  hugo: person('hugo', 'Hugo Martin', [ROLE_VALIDATOR], [ACACIAS]),
  thomas: person('thomas', 'Thomas Girard', [ROLE_VALIDATOR], [LAC]),
  lucie: person('lucie', 'Lucie Bernard', [ROLE_APPROVER], [TILLEULS, ACACIAS]),
  nina: person('nina', 'Nina Roussel', [ROLE_SUPERVISION], [TILLEULS]),
  lea: person('lea', 'Léa Fontaine', [ROLE_VALIDATOR], [TILLEULS]),
  karim: person('karim', 'Karim Benali', [ROLE_VALIDATOR], [VOSGES]),
};

const ALL_FAMILIES = 'Toutes les familles';
const BUILDINGS = 'Entretien des bâtiments';
const P = MOCK_PEOPLE;

export const MOCK_TEAMS = {
  validators: {
    id: 'validators',
    name: 'Validateurs Lorraine',
    email: 'validateurs-lorraine@exemple.fr',
    role: ROLE_VALIDATOR,
    support: MOCK_SUPPORT,
    members: [P.paul, P.marie, P.sophie, P.hugo, P.claire],
    usages: [
      { establishment: TILLEULS, scope: BUILDINGS, designation: 'Validateurs N+1' },
      { establishment: ACACIAS, scope: ALL_FAMILIES, designation: 'Validateurs N+1' },
    ],
  } as MockTeam,
  worksValidators: {
    id: 'worksValidators',
    name: 'Validateurs travaux Lorraine',
    email: 'travaux-lorraine@exemple.fr',
    role: ROLE_VALIDATOR,
    support: MOCK_SUPPORT,
    members: [P.claire, P.paul],
    usages: [
      { establishment: TILLEULS, scope: BUILDINGS, designation: 'Validateurs N+2' },
      { establishment: ACACIAS, scope: ALL_FAMILIES, designation: 'Validateurs N+2' },
    ],
  } as MockTeam,
  accounting: {
    id: 'accounting',
    name: 'Compta Lorraine',
    email: 'compta-lorraine@exemple.fr',
    role: ROLE_ACCOUNTANT,
    support: MOCK_SUPPORT,
    members: [P.marie, P.julien],
    usages: [
      { establishment: TILLEULS, scope: ALL_FAMILIES, designation: 'Comptable de rattachement' },
      { establishment: ACACIAS, scope: ALL_FAMILIES, designation: 'Comptable de rattachement' },
    ],
  } as MockTeam,
  supervision: {
    id: 'supervision',
    name: 'Supervision Lorraine',
    email: 'supervision-lorraine@exemple.fr',
    role: ROLE_SUPERVISION,
    support: MOCK_SUPPORT,
    members: [P.nina, P.paul],
    usages: [{ establishment: TILLEULS, scope: BUILDINGS, designation: 'Supervision métier' }],
  } as MockTeam,
};

export const ALL_TEAMS: MockTeam[] = Object.values(MOCK_TEAMS);
export const ALL_PEOPLE: MockPerson[] = Object.values(MOCK_PEOPLE);

export function establishmentNames(establishments: MockEstablishment[]): string {
  return establishments.map(establishment => establishment.name).join(', ');
}

/** Pourquoi une personne ne peut pas être membre de l'équipe, ou null si elle remplit les conditions. */
/** Ce que les règles d'éligibilité lisent d'une équipe : son rôle et son établissement support. */
export type TeamRules = Pick<MockTeam, 'role' | 'support'>;

/** Une équipe en cours de création ou de modification. */
export type TeamDraft = Omit<MockTeam, 'id' | 'usages'>;

export function memberIssue(member: MockPerson, team: TeamRules): string | null {
  if (!member.roles.includes(team.role)) {
    return `N’a pas le rôle ${team.role}`;
  }
  const inScope = member.establishments.some(establishment => team.support.clients.includes(establishment));
  return inScope ? null : `Affectation hors périmètre : ${establishmentNames(member.establishments)}`;
}

/** Pourquoi un membre ne peut pas agir sur un document de cet établissement, ou null s'il peut agir. */
export function actionIssue(member: MockPerson, team: MockTeam, establishment: MockEstablishment): string | null {
  if (!team.members.includes(member)) {
    return 'Vous n’êtes pas membre de l’équipe';
  }
  if (!member.roles.includes(team.role)) {
    return `Vous n’avez pas le rôle ${team.role}`;
  }
  return member.establishments.includes(establishment)
    ? null
    : `Vous n’avez pas d’affectation sur ${establishment.name}`;
}

/** Équipes qu'on peut désigner pour ce rôle dans cet établissement client. */
export function teamsFor(role: string, establishment: MockEstablishment): MockTeam[] {
  return ALL_TEAMS.filter(team => team.role === role && team.support.clients.includes(establishment));
}

export function teamsOf(member: MockPerson): MockTeam[] {
  return ALL_TEAMS.filter(team => team.members.includes(member));
}

export function membersWithIssue(team: MockTeam): MockPerson[] {
  return team.members.filter(member => memberIssue(member, team) !== null);
}

/** Infobulle d'une équipe : « Membres : Paul Renaud, Marie Dupré ». */
export function teamMembersLabel(team: MockTeam): string {
  return `Membres : ${team.members.map(member => member.fullName).join(', ')}`;
}

export function personInitials(member: MockPerson): string {
  return member.fullName
    .split(' ')
    .map(part => part[0])
    .join('');
}

/** Nom court d'un établissement : « Les Acacias ». */
export function shortName(establishment: MockEstablishment): string {
  return establishment.name.replace(/^Résidence /, '');
}

export const SUB_BASKET_TO_AUTHORIZE = 'Sous-panier à autoriser';

/** Actions en attente fictives : la plupart attendues de « Validateurs Lorraine ». */
export const PENDING_ACTIONS: MockPendingAction[] = [
  {
    id: 'a1',
    action: SUB_BASKET_TO_AUTHORIZE,
    reference: 'PAN-2026-000412-001',
    establishment: ACACIAS,
    team: MOCK_TEAMS.validators,
    since: '27/10/2026',
    waitingDays: 0,
    claimedBy: P.paul,
    claimedAt: '27/10 à 09:12',
  },
  {
    id: 'a2',
    action: SUB_BASKET_TO_AUTHORIZE,
    reference: 'PAN-2026-000398-002',
    establishment: TILLEULS,
    team: MOCK_TEAMS.validators,
    since: '22/10/2026',
    waitingDays: 5,
  },
  {
    id: 'a3',
    action: SUB_BASKET_TO_AUTHORIZE,
    reference: 'PAN-2026-000405-001',
    establishment: ACACIAS,
    team: MOCK_TEAMS.validators,
    since: '23/10/2026',
    waitingDays: 4,
    claimedBy: P.hugo,
    claimedAt: '24/10 à 14:30',
  },
  {
    id: 'a4',
    action: SUB_BASKET_TO_AUTHORIZE,
    reference: 'PAN-2026-000410-003',
    establishment: TILLEULS,
    team: MOCK_TEAMS.validators,
    since: '26/10/2026',
    waitingDays: 1,
    claimedBy: P.claire,
    claimedAt: '26/10 à 16:05',
  },
  {
    id: 'a5',
    action: 'Facture à approuver',
    reference: 'FAC-2026-000131',
    establishment: ACACIAS,
    person: P.paul,
    since: '26/10/2026',
    waitingDays: 1,
  },
  {
    id: 'a6',
    action: 'Devis à valider',
    reference: 'DEV-2026-000057',
    establishment: ACACIAS,
    team: MOCK_TEAMS.worksValidators,
    since: '25/10/2026',
    waitingDays: 2,
    claimedBy: P.claire,
    claimedAt: '25/10 à 11:40',
  },
  {
    id: 'a7',
    action: SUB_BASKET_TO_AUTHORIZE,
    reference: 'PAN-2026-000420-001',
    establishment: TILLEULS,
    person: P.lea,
    since: '27/10/2026',
    waitingDays: 0,
  },
];

export function canAct(member: MockPerson, team: MockTeam, establishment: MockEstablishment): boolean {
  return actionIssue(member, team, establishment) === null;
}

/** Ce qui attend la personne elle-même, ou qu'elle a pris en charge. */
export function actionsForMe(member: MockPerson): MockPendingAction[] {
  return PENDING_ACTIONS.filter(item => item.person === member || item.claimedBy === member);
}

/** Ce qui attend ses équipes là où elle peut agir, hors ce qu'elle a déjà pris en charge. */
export function actionsForMyTeams(member: MockPerson): MockPendingAction[] {
  return PENDING_ACTIONS.filter(
    item => item.team && item.claimedBy !== member && canAct(member, item.team, item.establishment),
  );
}

export function actionsOfTeam(team: MockTeam): MockPendingAction[] {
  return PENDING_ACTIONS.filter(item => item.team === team);
}

/** Membres de l'équipe qui peuvent agir sur l'action (attribution par le référent). */
export function assigneesOf(item: MockPendingAction): MockPerson[] {
  const team = item.team;
  return team ? team.members.filter(member => canAct(member, team, item.establishment)) : [];
}

/** « Paul Renaud s'en occupe depuis le 27/10 à 09:12 », vu par une personne donnée. */
export function claimLabel(claimedBy: MockPerson | undefined, claimedAt: string, viewer?: MockPerson): string {
  if (!claimedBy) {
    return 'Personne ne s’en occupe encore';
  }
  const who = claimedBy === viewer ? 'Vous vous en occupez' : `${claimedBy.fullName} s’en occupe`;
  return `${who} depuis le ${claimedAt}`;
}

export const NEED_LINES: string[] = [
  'Une action attendue doit pouvoir concerner une équipe, pas une seule personne.',
  'La notification part vers une adresse commune.',
  'Une fois l’action traitée, elle s’éteint pour les autres.',
];

export const TEAM_OPEN_QUESTIONS: OpenQuestion[] = [
  {
    question: 'Une même désignation peut-elle mêler une équipe et des personnes ?',
    note: 'La maquette suppose que oui.',
  },
  {
    question: 'L’adresse commune est-elle obligatoire ?',
    note: 'La maquette suppose que oui : c’est le seul destinataire du mail.',
  },
  {
    question: 'Si l’établissement support perd un client où l’équipe est désignée, que devient la désignation ?',
    note: 'La maquette suppose qu’elle reste, signalée comme un membre qui ne remplit plus les conditions.',
  },
  {
    question: 'Comment chaque membre voit-il dans Predik ce qui l’attend ?',
    note: 'À traiter avec les notifications et le tableau de bord.',
  },
];

export const TEAM_ACTION_OPTIONS: TeamActionOption[] = [
  {
    id: 'equipes',
    title: 'Les équipes',
    summary: 'Une équipe porte un rôle pour les établissements clients de son établissement support.',
    principle:
      'Une équipe a un nom, une adresse commune, un rôle et un établissement support. Elle se désigne comme une personne, dans les établissements clients de son établissement support.',
    ready: true,
    tabs: [
      {
        id: 'equipes',
        label: 'LES ÉQUIPES',
        screens: [
          {
            key: 'team-list',
            caption: 'Paramétrage : toutes les équipes, avec leur rôle et leur établissement support.',
          },
        ],
      },
      {
        id: 'fiche',
        label: 'FICHE ÉQUIPE',
        screens: [
          {
            key: 'team-details',
            caption: 'Fiche de l’équipe « Validateurs Lorraine » : périmètre déduit, membres et désignations.',
          },
        ],
      },
      {
        id: 'support',
        label: 'ÉTABLISSEMENT SUPPORT',
        screens: [
          {
            key: 'support-establishment',
            caption: 'Fiche de l’établissement support : un onglet liste les équipes qui lui sont rattachées.',
          },
        ],
      },
      {
        id: 'utilisateur',
        label: 'UTILISATEUR',
        screens: [
          {
            key: 'user-details',
            caption: 'Fiche de Paul Renaud : les équipes dont il est membre.',
          },
        ],
      },
      {
        id: 'affectation',
        label: 'AFFECTATION',
        screens: [
          {
            key: 'purchase-actors',
            caption: 'Acteurs achats de Résidence Les Tilleuls : une équipe se désigne comme une personne.',
          },
          {
            key: 'establishment',
            caption: 'Fiche de Résidence Les Tilleuls : l’équipe « Compta Lorraine » est comptable de rattachement.',
          },
          {
            key: 'sub-basket',
            mode: 'delegation',
            caption: 'Sous-panier : l’autorisation se délègue à une équipe.',
          },
        ],
      },
      {
        id: 'action',
        label: 'NOTIFICATION ET ACTION',
        screens: [
          {
            key: 'team-mail',
            caption: 'Boîte commune de l’équipe : le mail d’un sous-panier de Résidence Les Acacias.',
          },
          {
            key: 'sub-basket',
            mode: 'action',
            caption: 'Le même sous-panier vu par deux membres : seul celui qui est affecté aux Acacias peut agir.',
          },
        ],
      },
      {
        id: 'facture',
        label: 'FACTURE',
        screens: [
          {
            key: 'bill',
            caption: 'Facture vue par Marie Dupré, membre de « Compta Lorraine » : elle agit pour l’équipe.',
          },
        ],
      },
      {
        id: 'questions',
        label: 'QUESTIONS OUVERTES',
        screens: [
          {
            key: 'open-questions',
            caption: 'Points restant à trancher, avec l’hypothèse retenue par la maquette.',
          },
        ],
      },
    ],
    asideTitle: 'Les règles',
    rules: [
      'Une équipe a un nom, une adresse commune, un rôle et un établissement support.',
      'Son périmètre : les établissements clients de son établissement support.',
      'Plusieurs équipes peuvent partager le même rôle et le même établissement support.',
      'Pour entrer dans l’équipe : avoir son rôle et être affecté à au moins un établissement du périmètre.',
      'Elle se désigne seulement dans les établissements clients de son établissement support.',
      'Le mail part à l’adresse commune de l’équipe.',
      'Pour agir : être membre, avoir le rôle et être affecté à l’établissement du document.',
      'Un membre qui ne remplit plus les conditions reste dans l’équipe, signalé par une icône d’erreur.',
      'On peut déléguer une action à une équipe.',
      'Les équipes se gèrent selon les droits d’accès de chacun.',
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications et tableau de bord',
    summary: 'Des pistes pour que chacun voie ce qui l’attend, en personne ou via ses équipes.',
    principle:
      'Des pistes reliées aux équipes, pour que chacun sache ce qui l’attend et voie qui s’en occupe. Elles se combinent librement.',
    ready: true,
    tabs: [
      {
        id: 'recevoir',
        label: 'QUI REÇOIT',
        screens: [
          {
            key: 'recipients',
            mode: 'shared-only',
            piste: 1,
            caption: 'Adresse commune seule : seule la boîte de l’équipe reçoit le mail du sous-panier.',
          },
          {
            key: 'recipients',
            mode: 'who-can-act',
            piste: 2,
            caption:
              'Adresse commune, et dans Predik seulement les membres qui peuvent agir sur Résidence Les Acacias.',
          },
          {
            key: 'notification-preferences',
            piste: 3,
            caption: 'Préférences de Claire Petit : un mail personnel en plus, et les établissements qu’elle suit.',
          },
          {
            key: 'absence',
            piste: 4,
            caption: 'Absence de Paul Renaud : ses équipes continuent, ce qu’il avait pris en charge est libéré.',
          },
        ],
      },
      {
        id: 'doublon',
        label: 'PAS DE DOUBLON',
        screens: [
          {
            key: 'sub-basket',
            mode: 'claim',
            piste: 5,
            caption: '« Je m’en occupe » : la prise en charge est visible des autres membres.',
          },
          {
            key: 'mailbox',
            mode: 'follow-up',
            piste: 6,
            caption: 'Boîte commune : le mail de l’action, puis « pris en charge », puis « traité ».',
          },
          {
            key: 'notification-list',
            piste: 7,
            caption: 'Notifications de Hugo Martin : celles que d’autres ont prises ou traitées changent d’état.',
          },
          {
            key: 'sub-basket',
            mode: 'from-mail',
            piste: 8,
            caption: 'Document ouvert depuis le mail : on voit tout de suite si l’on peut agir et qui s’en occupe.',
          },
        ],
      },
      {
        id: 'attente',
        label: 'VOIR CE QUI ATTEND',
        screens: [
          {
            key: 'bell',
            piste: 9,
            caption: 'Cloche de Paul Renaud : ce qui l’attend lui, et ce qui attend ses équipes là où il peut agir.',
          },
          {
            key: 'team-board',
            piste: 10,
            caption:
              'Vue de l’équipe « Validateurs Lorraine » : ce qui l’attend, par établissement, et qui s’en occupe.',
          },
          {
            key: 'action-list',
            piste: 11,
            caption: 'Liste des sous-paniers vue par Paul Renaud : colonne « En charge » et filtre « Attendu de ».',
          },
        ],
      },
      {
        id: 'relance',
        label: 'NE RIEN LAISSER TRAÎNER',
        screens: [
          {
            key: 'team-digest',
            piste: 12,
            caption: 'Résumé quotidien à l’adresse commune, les actions les plus anciennes d’abord.',
          },
          {
            key: 'team-reminder',
            piste: 13,
            caption: 'Relance : une action sans prise en charge depuis plus de 3 jours est rappelée à l’équipe.',
          },
          {
            key: 'team-lead',
            piste: 14,
            caption: 'Vue de la référente, Marie Dupré : elle reçoit les relances et attribue une action à un membre.',
          },
        ],
      },
      {
        id: 'boite',
        label: 'BOÎTE COMMUNE',
        screens: [
          {
            key: 'mailbox',
            mode: 'subjects',
            piste: 15,
            caption: 'Objets de mail normalisés : la boîte commune se trie par équipe et par établissement.',
          },
        ],
      },
    ],
    asideTitle: 'À savoir',
    rules: [
      'Ce sont des pistes : aucune n’est retenue à ce stade.',
      'Elles se combinent librement.',
      'Presque toutes reposent sur un même socle : chaque action attendue est enregistrée avec son état (à faire, prise en charge, traitée).',
      'Les pistes 5, 6 et 7 couvrent ensemble « le premier qui traite, ça s’éteint pour les autres ».',
      'Les pistes 13 et 14 forment un même dispositif de relance.',
      'Les pistes 2 et 9 séparent ce que je peux faire de ce que mon équipe a à faire.',
    ],
  },
];

export function findOption(id: string | null): TeamActionOption | undefined {
  return TEAM_ACTION_OPTIONS.find(option => option.id === id);
}
